// EditSiteDrawer — the site RECORD's edit surface for NON-DRAFT sites (a draft's
// edit surface is the setup wizard — the locked draft model). The ADR-038 drawer
// rule: the section reuses a field group shared with the entity's create flow
// (SiteRecordFields — create · resume · edit are one control), so the drawer is
// the container, the body is shared. site:write; PATCH on save — the WHOLE
// section, the form's own choice; the sites prefix invalidates so the list + peek
// + detail refetch together. The edit rides the row stamp (ADR-084): the shell
// seeds from the site as it is at each open, the PATCH carries the stamp, and a
// stale save shows the line with Load the latest (the site's detail re-read).
// Mounted by its hosts only while OPEN — nothing of its lists loads for a closed
// drawer (ADR-080 §7); each picker loads on the press, naming what the site
// holds first.

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { UseFormReturn } from 'react-hook-form';

import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../components/patterns/forms/useFormShell.js';
import { fetchSiteDetail, sitesKeys, useUpdateSite } from '../../hooks/useSites.js';
import type { SiteDetail } from '@ems-app/schemas/sites/responses';
import {
  SiteRecordFields,
  siteRecordPatch,
  siteRecordShape,
  siteRecordValuesFrom,
  type SiteRecordValues,
} from './SiteRecordFields.js';

const editSchema = z.object(siteRecordShape) as unknown as z.ZodType<SiteRecordValues, SiteRecordValues>;

export function EditSiteDrawer({
  projectId,
  site,
  open,
  onClose,
}: {
  projectId: string;
  site: SiteDetail;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('sites');
  const qc = useQueryClient();
  const update = useUpdateSite(projectId);

  const shell = useFormShell<SiteRecordValues, SiteDetail>({
    schema: editSchema,
    row: site,
    values: (r) => siteRecordValuesFrom(r),
    // The fresh site: a quiet read (a site gone meanwhile is said in the form), into the page's entry once it answers.
    reload: () => readLatest(qc, sitesKeys.detail(projectId, site.id), () => fetchSiteDetail(projectId, site.id, true)),
    onSubmit: async (v, { stamp }) => {
      await update.mutateAsync({ siteId: site.id, body: { ...siteRecordPatch(v), row_version: stamp } });
    },
    onSuccess: onClose,
  });

  return (
    <DrawerForm
      shell={shell}
      open={open}
      onClose={onClose}
      title={t('actions.edit')}
      description={site.name}
      submitLabel={t('common:actions.save')}
      maxWidth="max-w-xl"
    >
      <SiteRecordFields
        form={shell.form as unknown as UseFormReturn<SiteRecordValues>}
        projectId={projectId}
        t={t}
        // Activation markers are a draft-only guide; a live/suspended/decommissioned
        // site has already cleared activation, so the dots + legend would be noise.
        activationMarkers={site.status === 'draft'}
        // A non-draft site's classification is SCD-managed (ADR-060 §3) — it changes
        // through the designation timeline, never the record drawer.
        designations={false}
      />
    </DrawerForm>
  );
}
